import type { CssVariableMap, DesignThemeTokens } from './types';

const assign = (target: CssVariableMap, prefix: string, value: string): void => {
  target[prefix] = value;
};

/** Maps semantic tokens to `--ds-*` CSS custom properties. */
export const toSemanticCssVariables = (tokens: DesignThemeTokens): CssVariableMap => {
  const vars: CssVariableMap = {};
  const { color, blur, radius, spacing, typography, motion, shadow } = tokens;

  assign(vars, '--ds-bg-primary', color.background.primary);
  assign(vars, '--ds-bg-secondary', color.background.secondary);
  assign(vars, '--ds-bg-tertiary', color.background.tertiary);
  assign(vars, '--ds-bg-inverse', color.background.inverse);

  assign(vars, '--ds-surface-base', color.surface.base);
  assign(vars, '--ds-surface-elevated', color.surface.elevated);
  assign(vars, '--ds-surface-grouped', color.surface.grouped);
  assign(vars, '--ds-surface-chrome', color.surface.chrome);
  assign(vars, '--ds-surface-modal', color.surface.modal);
  assign(vars, '--ds-surface-overlay', color.surface.overlay);
  assign(vars, '--ds-surface-overlay-modal', color.surface.overlayModal);

  assign(vars, '--ds-border-hairline', color.border.hairline);
  assign(vars, '--ds-border-primary', color.border.primary);
  assign(vars, '--ds-border-secondary', color.border.secondary);
  assign(vars, '--ds-border-opaque', color.border.opaque);

  assign(vars, '--ds-text-primary', color.text.primary);
  assign(vars, '--ds-text-secondary', color.text.secondary);
  assign(vars, '--ds-text-tertiary', color.text.tertiary);
  assign(vars, '--ds-text-quaternary', color.text.quaternary);
  assign(vars, '--ds-text-inverse', color.text.inverse);
  assign(vars, '--ds-text-on-accent', color.text.onAccent);

  assign(vars, '--ds-fill-primary', color.fill.primary);
  assign(vars, '--ds-fill-secondary', color.fill.secondary);
  assign(vars, '--ds-fill-tertiary', color.fill.tertiary);
  assign(vars, '--ds-fill-quaternary', color.fill.quaternary);

  assign(vars, '--ds-link', color.link.default);
  assign(vars, '--ds-link-hover', color.link.hover);

  assign(vars, '--ds-semantic-success', color.semantic.success);
  assign(vars, '--ds-semantic-success-muted', color.semantic.successMuted);
  assign(vars, '--ds-semantic-warning', color.semantic.warning);
  assign(vars, '--ds-semantic-warning-muted', color.semantic.warningMuted);
  assign(vars, '--ds-semantic-error', color.semantic.error);
  assign(vars, '--ds-semantic-error-muted', color.semantic.errorMuted);
  assign(vars, '--ds-semantic-info', color.semantic.info);
  assign(vars, '--ds-semantic-info-muted', color.semantic.infoMuted);

  assign(vars, '--ds-control-primary-bg', color.control.primaryBg);
  assign(vars, '--ds-control-primary-text', color.control.primaryText);
  assign(vars, '--ds-control-secondary-bg', color.control.secondaryBg);
  assign(vars, '--ds-control-secondary-text', color.control.secondaryText);
  assign(vars, '--ds-control-secondary-border', color.control.secondaryBorder);
  assign(vars, '--ds-control-outline-bg', color.control.outlineBg);
  assign(vars, '--ds-control-outline-text', color.control.outlineText);
  assign(vars, '--ds-control-outline-border', color.control.outlineBorder);

  assign(vars, '--ds-input-bg', color.input.bg);
  assign(vars, '--ds-input-border', color.input.border);
  assign(vars, '--ds-input-border-focus', color.input.borderFocus);
  assign(vars, '--ds-input-text', color.input.text);
  assign(vars, '--ds-input-placeholder', color.input.placeholder);

  assign(vars, '--ds-icon-primary', color.icon.primary);
  assign(vars, '--ds-icon-secondary', color.icon.secondary);
  assign(vars, '--ds-icon-inverse', color.icon.inverse);

  assign(vars, '--ds-hover-primary', color.interaction.hoverPrimary);
  assign(vars, '--ds-hover-secondary', color.interaction.hoverSecondary);
  assign(vars, '--ds-focus-ring', color.interaction.focusRing);
  assign(vars, '--ds-selection', color.interaction.selection);

  assign(vars, '--ds-blur-chrome', blur.chrome);
  assign(vars, '--ds-chrome-saturate', blur.chromeSaturate);
  assign(vars, '--ds-blur-overlay', blur.overlay);

  assign(vars, '--ds-radius-control', radius.control);
  assign(vars, '--ds-radius-panel', radius.panel);
  assign(vars, '--ds-radius-sheet', radius.sheet);
  assign(vars, '--ds-radius-pill', radius.pill);

  assign(vars, '--ds-space-xs', spacing.xs);
  assign(vars, '--ds-space-sm', spacing.sm);
  assign(vars, '--ds-space-md', spacing.md);
  assign(vars, '--ds-space-lg', spacing.lg);
  assign(vars, '--ds-space-xl', spacing.xl);
  assign(vars, '--ds-space-xxl', spacing.xxl);

  assign(vars, '--ds-font-title-lg', typography.titleLg);
  assign(vars, '--ds-font-title-md', typography.titleMd);
  assign(vars, '--ds-font-title-sm', typography.titleSm);
  assign(vars, '--ds-font-body', typography.body);
  assign(vars, '--ds-font-callout', typography.callout);
  assign(vars, '--ds-font-caption', typography.caption);
  assign(vars, '--ds-font-weight-regular', typography.weightRegular);
  assign(vars, '--ds-font-weight-medium', typography.weightMedium);
  assign(vars, '--ds-font-weight-semibold', typography.weightSemibold);

  assign(vars, '--ds-motion-fast', motion.fast);
  assign(vars, '--ds-motion-normal', motion.normal);
  assign(vars, '--ds-motion-panel', motion.panel);
  assign(vars, '--ds-motion-easing-standard', motion.easingStandard);
  assign(vars, '--ds-motion-easing-emphasized', motion.easingEmphasized);

  assign(vars, '--ds-shadow-sm', shadow.sm);
  assign(vars, '--ds-shadow-md', shadow.md);
  assign(vars, '--ds-shadow-lg', shadow.lg);

  return vars;
};

/**
 * Bridge to existing `--color-*` variables used across the app.
 * Accent-related vars are still overridden dynamically in useTheme.
 */
export const toLegacyCssVariables = (tokens: DesignThemeTokens): CssVariableMap => {
  const { color, radius, motion, shadow } = tokens;

  return {
    '--color-bg-primary': color.background.primary,
    '--color-bg-secondary': color.background.secondary,
    '--color-bg-tertiary': color.background.tertiary,
    '--color-bg-inverse': color.background.inverse,

    '--color-surface-primary': color.surface.base,
    '--color-surface-secondary': color.surface.elevated,
    '--color-surface-tertiary': color.surface.grouped,
    '--color-surface-overlay': color.surface.overlay,

    '--color-border-primary': color.border.primary,
    '--color-border-secondary': color.border.secondary,
    '--color-border-subtle': color.border.hairline,

    '--color-text-primary': color.text.primary,
    '--color-text-secondary': color.text.secondary,
    '--color-text-tertiary': color.text.tertiary,
    '--color-text-inverse': color.text.inverse,
    '--color-text-on-accent': color.text.onAccent,

    '--color-btn-secondary-bg': color.control.secondaryBg,
    '--color-btn-secondary-text': color.control.secondaryText,
    '--color-btn-secondary-hover': color.interaction.hoverSecondary,
    '--color-btn-secondary-border': color.control.secondaryBorder,

    '--color-btn-outline-bg': color.control.outlineBg,
    '--color-btn-outline-text': color.control.outlineText,
    '--color-btn-outline-border': color.control.outlineBorder,
    '--color-btn-outline-hover': color.interaction.hoverPrimary,

    '--color-input-bg': color.input.bg,
    '--color-input-border': color.input.border,
    '--color-input-border-focus': color.input.borderFocus,
    '--color-input-text': color.input.text,
    '--color-input-placeholder': color.input.placeholder,

    '--color-icon-primary': color.icon.primary,
    '--color-icon-secondary': color.icon.secondary,
    '--color-icon-inverse': color.icon.inverse,

    '--color-hover-primary': color.interaction.hoverPrimary,
    '--color-hover-secondary': color.interaction.hoverSecondary,
    '--color-focus-ring': color.interaction.focusRing,
    '--color-selection': color.interaction.selection,

    '--color-link': color.link.default,
    '--color-link-hover': color.link.hover,

    '--color-badge-success-bg': color.semantic.successMuted,
    '--color-badge-success-text': color.semantic.success,
    '--color-badge-warning-bg': color.semantic.warningMuted,
    '--color-badge-warning-text': color.semantic.warning,
    '--color-badge-error-bg': color.semantic.errorMuted,
    '--color-badge-error-text': color.semantic.error,
    '--color-badge-info-bg': color.semantic.infoMuted,
    '--color-badge-info-text': color.semantic.info,

    '--radius-sm': radius.control,
    '--radius-md': radius.panel,
    '--radius-lg': radius.sheet,
    '--radius-xl': radius.sheet,

    '--transition-fast': `${motion.fast} ${motion.easingStandard}`,
    '--transition-normal': `${motion.normal} ${motion.easingStandard}`,
    '--transition-slow': `${motion.panel} ${motion.easingEmphasized}`,

    '--shadow-sm': shadow.sm,
    '--shadow-md': shadow.md,
    '--shadow-lg': shadow.lg,
  };
};

export const toCssVariables = (tokens: DesignThemeTokens): CssVariableMap => ({
  ...toSemanticCssVariables(tokens),
  ...toLegacyCssVariables(tokens),
});
